import { DefaultButton, MessageBar, MessageBarType, Panel, PanelType, Stack } from '@fluentui/react';
import React from 'react';
import { useAddProcurementDemandItemToOrderMutation, useCreateProcurementOrderMutation } from '../../../store/endpoints/ProcurementEndpoints';
import OptionSelectionToggle from '../../Common/OptionSelectionToggle';
import AccordionBox from '../../Common/AccordionBox';
import { ICreateProcurementOrderRequest, IProcurementDemandItem } from '../../../models/Procurement';
import { BillToStep, FindExistingOrderStep, OrderDetailsStep, ShipToStep, STEP_NONE, STEP_REVIEW } from './ProcureDemandPanel';
import { commonShipTo } from '../useDefaultShipmentContact';

interface IProcureDemandMultiPanelProps {
    demands: IProcurementDemandItem[];
    onDismiss: () => void;
    onProcure: (orderId: string) => void;
}

const PROCUREMENT_METHOD_NEW_ORDER = 'Create Order';
const PROCUREMENT_METHOD_ADD_TO_EXISTING = 'Add to Order';

const ProcureDemandMultiPanel: React.FC<IProcureDemandMultiPanelProps> = (props) => {
    const [createOrder] = useCreateProcurementOrderMutation();
    const [addToOrder] = useAddProcurementDemandItemToOrderMutation();
    const [method, setMethod] = React.useState<string>(PROCUREMENT_METHOD_NEW_ORDER);
    const [currentStep, setCurrentStep] = React.useState<string>(STEP_REVIEW);
    const [createOrderDetails, setCreateOrderDetails] = React.useState<Partial<ICreateProcurementOrderRequest>>({});
    const [existingOrderId, setExistingOrderId] = React.useState<string | undefined>(undefined);
    const [error, setError] = React.useState<string | undefined>(undefined);
    const [processingMessage, setProcessingMessage] = React.useState<string | undefined>(undefined);

    // All selected demands share the same vendor (enforced by the caller).
    const vendor = props.demands[0]?.vendor;
    // Only carry a ship-to forward when every demand agrees on it.
    const dropshipShipTo = commonShipTo(props.demands);

    const procure = async () => {
        if (props.demands.length === 0) return;
        setError(undefined);
        if (method === PROCUREMENT_METHOD_NEW_ORDER) {
            if (!vendor) return;
            setProcessingMessage('Creating procurement order...');
            const result = await createOrder({
                order: {
                    vendor: vendor,
                    ...createOrderDetails,
                    items: props.demands.map(demand => ({
                        catalog: demand.catalog,
                        productId: demand.productId,
                        quantity: demand.quantity,
                        procurementDemandId: demand.id,
                    }))
                }
            });
            setProcessingMessage(undefined);
            if (!('data' in result) || !result.data.id) {
                setError('Error creating procurement order');
                return;
            }
            props.onProcure(result.data.id);
        } else if (method === PROCUREMENT_METHOD_ADD_TO_EXISTING) {
            if (!existingOrderId) return;
            setProcessingMessage('Adding items to order...');
            for (const demand of props.demands) {
                const result = await addToOrder({
                    itemId: demand.id,
                    orderId: existingOrderId,
                    data: {}
                });
                if (!('data' in result) || !result.data.success) {
                    setProcessingMessage(undefined);
                    setError('Error adding items to order');
                    return;
                }
            }
            setProcessingMessage(undefined);
            props.onProcure(existingOrderId);
        }
    }

    const validate = () => {
        if (props.demands.length === 0) return false;
        if (method === PROCUREMENT_METHOD_NEW_ORDER) {
            return !!vendor;
        }
        if (method === PROCUREMENT_METHOD_ADD_TO_EXISTING) {
            return !!existingOrderId;
        }
        return false;
    }

    return (
        <Panel
            type={PanelType.medium}
            isOpen={true}
            onDismiss={props.onDismiss}
            isFooterAtBottom={true}
            headerText={`Procure ${props.demands.length} Demand Items`}
            onRenderFooter={() => <Stack horizontal tokens={{ childrenGap: '1em' }} style={{ padding: '1em' }}>
                <DefaultButton onClick={props.onDismiss} style={{ flexGrow: 1 }} disabled={processingMessage !== undefined}>Cancel</DefaultButton>
                <DefaultButton primary onClick={procure} style={{ flexGrow: 1 }} disabled={!validate() || processingMessage !== undefined}>{processingMessage ?? 'Procure'}</DefaultButton>
            </Stack>}
        >
            {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
            <Stack style={{ paddingTop: '1em' }}>
                <OptionSelectionToggle
                    options={[
                        { key: PROCUREMENT_METHOD_NEW_ORDER, text: PROCUREMENT_METHOD_NEW_ORDER },
                        { key: PROCUREMENT_METHOD_ADD_TO_EXISTING, text: PROCUREMENT_METHOD_ADD_TO_EXISTING }
                    ]}
                    selectedKey={method}
                    onChange={setMethod}
                />
                <AccordionBox
                    title={STEP_REVIEW}
                    secondaryTitle={vendor}
                    isExpanded={currentStep === STEP_REVIEW}
                    onToggle={(expanded, itemKey) => {
                        if (itemKey === undefined) return;
                        if (!expanded) setCurrentStep(STEP_NONE);
                        else setCurrentStep(itemKey);
                    }}
                    itemKey={STEP_REVIEW}
                >
                    <table className='data-table'>
                        <thead>
                            <tr>
                                <th>Product</th>
                                <th>Manufacturer</th>
                                <th>Quantity</th>
                            </tr>
                        </thead>
                        <tbody>
                            {props.demands.map(demand => (
                                <tr key={demand.id}>
                                    <td>{demand.productName}</td>
                                    <td>{demand.manufacturer}</td>
                                    <td>{demand.quantity}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </AccordionBox>
                {method === PROCUREMENT_METHOD_NEW_ORDER ? (
                    <>
                        <OrderDetailsStep
                            details={createOrderDetails}
                            onChange={setCreateOrderDetails}
                            step={currentStep}
                            setStep={setCurrentStep}
                        />
                        <ShipToStep
                            shipTo={createOrderDetails.shipTo}
                            onChange={(shipTo) => setCreateOrderDetails({ ...createOrderDetails, shipTo })}
                            dropship={createOrderDetails.dropship}
                            dropshipShipTo={dropshipShipTo}
                            step={currentStep}
                            setStep={setCurrentStep}
                        />
                        <BillToStep
                            billTo={createOrderDetails.billTo}
                            onChange={(billTo) => setCreateOrderDetails({ ...createOrderDetails, billTo })}
                            step={currentStep}
                            setStep={setCurrentStep}
                        />
                    </>
                ) : null}
                {method === PROCUREMENT_METHOD_ADD_TO_EXISTING ? (
                    <FindExistingOrderStep
                        selectedOrderId={existingOrderId}
                        onOrderSelected={setExistingOrderId}
                        step={currentStep}
                        setStep={setCurrentStep}
                        vendor={vendor}
                    />
                ) : null}
            </Stack>
        </Panel>
    );
}

export default ProcureDemandMultiPanel;
